// spacing.ts

type Size = "xLarge" | "Large" | "Medium" | "Small" | "xSmall";
// type Breakpoint = 'mobile' | 'tablet' | 'desktop' | 'largeDesktop';

export type SpacingCssProperty =
    | "padding"
    | "padding-top"
    | "padding-right"
    | "padding-bottom"
    | "padding-left"
    | "padding-inline"
    | "padding-block"
    | "margin"
    | "margin-top"
    | "margin-right"
    | "margin-bottom"
    | "margin-left"
    | "margin-inline"
    | "margin-block";

type SpacingConfig = {
    [key in Breakpoint]: {
        [size in Size]: string;
    };
};

export const spacing = {
    mobile: {
        xLarge: "40px",
        Large: "32px",
        Medium: "24px",
        Small: "24px",
        xSmall: "24px",
        xxSmall: "16px",
    },
    tablet: {
        xLarge: "64px",
        Large: "40px",
        Medium: "32px",
        Small: "24px",
        xSmall: "24px",
        xxSmall: "16px",
    },
    desktop: {
        xLarge: "80px",
        Large: "64px",
        Medium: "40px",
        Small: "32px",
        xSmall: "24px",
        xxSmall: "16px",
    },
    largeDesktop: {
        xLarge: "80px",
        Large: "64px",
        Medium: "40px",
        Small: "32px",
        xSmall: "24px",
        xxSmall: "16px",
    },
} as const;

export const breakpoints = {
    tablet: "450px",
    desktop: "992px",
    largeDesktop: "1440px",
} as const;

type Breakpoint = keyof typeof spacing;
type SpacingKey = keyof typeof spacing.mobile;

/**
 * Generates responsive spacing rules based on defined breakpoints
 */
export const getResponsiveSpacing = (
  cssProps:
    | SpacingCssProperty
    | SpacingCssProperty[]
    | "px" | "py" | "mx" | "my"
    | "pl" | "pr" | "pt" | "pb"
    | "ml" | "mr" | "mt" | "mb"
    | Array<SpacingCssProperty | string>,
  size: SpacingKey | SpacingKey[]
): string => {
  const isCssArray = Array.isArray(cssProps);
  const isSizeArray = Array.isArray(size);

  const expandProp = (prop: string): SpacingCssProperty[] => {
    switch (prop) {
      case "px": return ["padding-left", "padding-right"];
      case "py": return ["padding-top", "padding-bottom"];
      case "mx": return ["margin-left", "margin-right"];
      case "my": return ["margin-top", "margin-bottom"];
      case "pl": return ["padding-left"];
      case "pr": return ["padding-right"];
      case "pt": return ["padding-top"];
      case "pb": return ["padding-bottom"];
      case "ml": return ["margin-left"];
      case "mr": return ["margin-right"];
      case "mt": return ["margin-top"];
      case "mb": return ["margin-bottom"];
      default: return [prop as SpacingCssProperty];
    }
  };

  const breakpointOrder: Breakpoint[] = ["mobile", "tablet", "desktop", "largeDesktop"];
  const rules: string[] = [];

  // Case 1: Both are arrays => map by index
  if (isCssArray && isSizeArray) {
    const propsArray = cssProps as Array<string>;
    const sizeArray = size as SpacingKey[];

    const maxLen = Math.max(propsArray.length, sizeArray.length);

    for (let i = 0; i < maxLen; i++) {
      const props = expandProp(propsArray[i] ?? propsArray[propsArray.length - 1]);
      const sz = sizeArray[i] ?? sizeArray[sizeArray.length - 1];

      breakpointOrder.forEach((bp, bpIndex) => {
        const value = spacing[bp][sz];
        const declarations = props.map(p => `${p}: ${value};`).join("\n");

        if (bp === "mobile") {
          rules.push(declarations);
        } else {
          rules.push(`@media (min-width: ${breakpoints[bp]}) {\n${declarations}\n}`);
        }
      });
    }

    return rules.join("\n");
  }

  // Case 2: One is array (or neither)
  const expandedProps = isCssArray
  ? (cssProps as Array<string>).reduce((acc, val) => acc.concat(expandProp(val)), [] as SpacingCssProperty[])
  : expandProp(cssProps as string);

  const sizeArray = isSizeArray ? size as SpacingKey[] : [size as SpacingKey];

  breakpointOrder.forEach((bp, index) => {
    const currentSize = sizeArray[index] ?? sizeArray[sizeArray.length - 1];
    const declarations = expandedProps.map(p => `${p}: ${spacing[bp][currentSize]};`).join("\n");

    if (bp === "mobile") {
      rules.push(declarations);
    } else {
      rules.push(`@media (min-width: ${breakpoints[bp]}) {\n${declarations}\n}`);
    }
  });

  return rules.join("\n");
};

